import { type ShortUrl } from '@air/api';
import {
  type PresentationContentBlockInput,
  type PresentationContentBlockOutput,
  type PresentationContentLinkItemInput,
  type PresentationContentLinkItemOutput,
  type ShortUrlResponse,
} from '@air/api/types';

type ShortUrlUpdatePayload = Parameters<typeof ShortUrl.update>[0];

export type ShortUrlMutationPresentationContent =
  | ShortUrlResponse['presentationContent']
  | ShortUrlUpdatePayload['presentationContent'];

const getShortUrlUpdateLinkItem = (
  link: PresentationContentLinkItemInput | PresentationContentLinkItemOutput,
): PresentationContentLinkItemInput => ({
  id: link.id,
  label: link.label,
  url: link.url,
  // the read shape's thumbnail carries a server-resolved `url`; the write shape must stay a pure
  // asset-version reference so the server re-resolves it on read (mirrors coverImage)
  ...(link.thumbnail ? { thumbnail: { assetId: link.thumbnail.assetId, versionId: link.thumbnail.versionId } } : {}),
});

/**
 * Rebuilds a single content block in the write shape. Exported so sub-page content
 * (`getShortUrlUpdatePresentationPages`) normalizes blocks through the same whitelist rather than
 * duplicating it.
 */
export const getShortUrlUpdateBlock = (
  block: PresentationContentBlockInput | PresentationContentBlockOutput,
): PresentationContentBlockInput => {
  switch (block.type) {
    case 'text':
      return {
        id: block.id,
        hidden: block.hidden,
        type: 'text',
        body: block.body,
        inset: block.inset,
      };
    case 'image':
      return {
        id: block.id,
        hidden: block.hidden,
        type: 'image',
        assetId: block.assetId,
        versionId: block.versionId,
        extendToEdges: block.extendToEdges,
        roundCorners: block.roundCorners,
      };
    case 'video':
      return {
        id: block.id,
        hidden: block.hidden,
        type: 'video',
        assetId: block.assetId,
        versionId: block.versionId,
        extendToEdges: block.extendToEdges,
        roundCorners: block.roundCorners,
        loop: block.loop,
        autoplay: block.autoplay,
        controls: block.controls,
        muted: block.muted,
      };
    case 'links':
      return {
        id: block.id,
        hidden: block.hidden,
        type: 'links',
        style: block.style,
        alignment: block.alignment,
        size: block.size,
        links: block.links.map(getShortUrlUpdateLinkItem),
      };
    case 'title':
    case 'assets':
    case 'files':
    case 'boards':
      return {
        id: block.id,
        hidden: block.hidden,
        type: block.type,
      };
  }
};

/**
 * Converts a `presentationContent` value from the read shape (or an already-write-shaped draft)
 * into the `ShortUrl.update` write shape. The read shape's media blocks and link thumbnails carry
 * server-resolved `url` fields the strict-whitelisted write path rejects, so every block is rebuilt
 * as a pure reference. Unlike `presentationSettings` (shallow-PATCH), `presentationContent` is
 * wholesale-replaced when present: an object replaces the stored page body, an explicit `null`
 * clears it, and `undefined` leaves it untouched — all three pass through here unchanged in intent.
 */
export const getShortUrlUpdatePresentationContent = (
  presentationContent: ShortUrlMutationPresentationContent,
): ShortUrlUpdatePayload['presentationContent'] => {
  if (presentationContent == null) {
    return presentationContent;
  }

  return {
    blocks: presentationContent.blocks.map(getShortUrlUpdateBlock),
  };
};
